import React, { useEffect, useRef, useState, useCallback } from 'react';
import Matter from 'matter-js';
import { ORB_TYPES, PHYSICS_CONFIG, STORAGE_KEYS, SPECIALS } from '../constants';
import { audioManager } from '../utils/audio';

interface GameCanvasProps {
  onScoreUpdate: (score: number) => void;
  onQueueUpdate: (nextIndex: number, futureIndex: number) => void;
  onGameOver: () => void;
  isPaused: boolean;
  initialScore: number;
  level: number;
}

interface Particle {
  id: number;
  x: number;
  y: number;
  vx: number;
  vy: number;
  life: number;
  maxLife: number;
  color: string;
  size: number;
  type: 'sparkle' | 'ring' | 'text' | 'debris' | 'smoke';
  text?: string;
}

const debrisCache = new Map<number, { offsets: number[], craters: {x:number, y:number, r:number}[] }>();

const getDebrisVisuals = (id: number, radius: number) => {
    if (debrisCache.has(id)) return debrisCache.get(id)!;
    const offsets = Array.from({length: 6}, () => (Math.random() * 0.3) + 0.85); 
    const craters = [];
    for(let i=0; i<3; i++) {
        craters.push({
            x: (Math.random() - 0.5) * radius,
            y: (Math.random() - 0.5) * radius,
            r: Math.random() * (radius * 0.2) + 2
        });
    }
    const data = { offsets, craters };
    debrisCache.set(id, data);
    return data;
};

const GameCanvas: React.FC<GameCanvasProps> = ({ 
  onScoreUpdate, 
  onQueueUpdate,
  onGameOver,
  isPaused,
  initialScore,
  level,
}) => {
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const engineRef = useRef<Matter.Engine | null>(null);
  
  const particlesRef = useRef<Particle[]>([]);
  const particleIdCounter = useRef(0);
  const shakeRef = useRef(0);
  const flashRef = useRef(0);
  const timeoutsRef = useRef<ReturnType<typeof setTimeout>[]>([]);
  
  const nextOrbIndexRef = useRef(0);
  const futureOrbIndexRef = useRef(0); 
  const canDropRef = useRef(true);
  const scoreRef = useRef(initialScore);
  const isMountedRef = useRef(true);
  const aimXRef = useRef(PHYSICS_CONFIG.GAME_WIDTH / 2);
  const prevLevelRef = useRef(level);
  
  // Spam Protection State
  const spamStreakRef = useRef(0);
  const lastDropTimeRef = useRef(0);
  const [isOverheated, setIsOverheated] = useState(false);
  const isOverheatedRef = useRef(false);

  const comboCounterRef = useRef(0);
  const lastMergeTimeRef = useRef(0);

  const [canDrop, setCanDrop] = useState<boolean>(true);
  const [aimX, setAimX] = useState<number>(PHYSICS_CONFIG.GAME_WIDTH / 2);

  useEffect(() => {
    if (level > prevLevelRef.current && engineRef.current) {
        prevLevelRef.current = level;
        audioManager.playLevelUpSound();
        shakeRef.current = 20;
        flashRef.current = 0.3;
        createFloatingText(PHYSICS_CONFIG.GAME_WIDTH/2, PHYSICS_CONFIG.GAME_HEIGHT/3, `STAGE ${level}`);
        
        const newGravity = PHYSICS_CONFIG.GRAVITY_Y + (level * 0.1);
        engineRef.current.gravity.y = Math.min(newGravity, 2.5);

        const debrisCount = Math.min(Math.floor(level / 2) + 1, 3);
        for (let i = 0; i < debrisCount; i++) {
            const timeout = setTimeout(() => {
                if(!engineRef.current || !isMountedRef.current) return;
                const rx = Math.random() * (PHYSICS_CONFIG.GAME_WIDTH - 60) + 30;
                const debris = createOrb(rx, -50, SPECIALS.DEBRIS.id);
                if (debris) {
                    Matter.World.add(engineRef.current.world, debris);
                    Matter.Body.setVelocity(debris, { x: (Math.random() - 0.5) * 5, y: 5 + Math.random() * 5 });
                }
            }, i * 400);
            timeoutsRef.current.push(timeout);
        }
    }
  }, [level]);

  const addParticle = (p: Omit<Particle, 'id'>) => {
    particlesRef.current.push({ ...p, id: particleIdCounter.current++ });
  };

  const createExplosionEffect = (x: number, y: number, color: string, scale = 1.0) => {
    for (let i = 0; i < 20 * scale; i++) {
      const angle = Math.random() * Math.PI * 2;
      const speed = Math.random() * 10 * scale + 3;
      addParticle({
        x, y, vx: Math.cos(angle) * speed, vy: Math.sin(angle) * speed,
        life: 1.0, maxLife: 1.0, color: color, size: Math.random() * 5 * scale + 1.5, type: 'sparkle'
      });
    }
    addParticle({ x, y, vx: 0, vy: 0, life: 0.8, maxLife: 0.8, color: color, size: 12 * scale, type: 'ring' });
  };

  const createFloatingText = (x: number, y: number, text: string, color: string = '#ffffff') => {
    addParticle({ x, y, vx: 0, vy: -1.5, life: 1.5, maxLife: 1.5, color, size: 24, type: 'text', text });
  };

  const getRandomOrbIndex = () => {
    const r = Math.random();
    if (r < 0.04) return SPECIALS.ANTIMATTER.id;
    if (r < 0.15) return SPECIALS.DEBRIS.id;
    return Math.floor(Math.random() * 4);
  };

  const saveGameState = useCallback(() => {
    if (!engineRef.current || !isMountedRef.current) return;
    const bodies = Matter.Composite.allBodies(engineRef.current.world)
      .filter(b => b.label.startsWith('orb-'))
      .map(b => ({
        x: b.position.x, y: b.position.y,
        angle: b.angle, label: b.label,
        velocity: b.velocity,
        angularVelocity: b.angularVelocity,
        id: b.id
      }));

    localStorage.setItem(STORAGE_KEYS.GAME_STATE, JSON.stringify({
      bodies, 
      score: scoreRef.current, 
      nextOrbIndex: nextOrbIndexRef.current,
      futureOrbIndex: futureOrbIndexRef.current
    }));
  }, []);

  const loadGameState = (World: any, engine: Matter.Engine) => {
    if (initialScore === 0) {
        localStorage.removeItem(STORAGE_KEYS.GAME_STATE);
        return false;
    }
    const saved = localStorage.getItem(STORAGE_KEYS.GAME_STATE);
    if (!saved) return false;
    try {
      const state = JSON.parse(saved);
      if (!state.bodies) return false;
      const newBodies = state.bodies.map((b: any) => {
        const index = parseInt(b.label.split('-')[1]);
        const orb = createOrb(b.x, b.y, index, false);
        if (orb) {
            Matter.Body.setAngle(orb, b.angle);
            Matter.Body.setVelocity(orb, b.velocity);
            Matter.Body.setAngularVelocity(orb, b.angularVelocity);
            return orb;
        }
        return null;
      }).filter(Boolean);
      World.add(engine.world, newBodies);
      scoreRef.current = state.score;
      onScoreUpdate(0); 
      nextOrbIndexRef.current = state.nextOrbIndex || 0;
      futureOrbIndexRef.current = state.futureOrbIndex || getRandomOrbIndex();
      onQueueUpdate(nextOrbIndexRef.current, futureOrbIndexRef.current);
      return true;
    } catch (e) { return false; }
  };

  const createOrb = (x: number, y: number, index: number, isStatic = false) => {
    if (index === SPECIALS.DEBRIS.id) {
        return Matter.Bodies.polygon(x, y, 6, SPECIALS.DEBRIS.radius, {
            label: `orb-${index}`, isStatic, restitution: 0.1, friction: 0.8, frictionStatic: 1.0, density: 0.02
        });
    }
    if (index === SPECIALS.ANTIMATTER.id) {
        return Matter.Bodies.circle(x, y, SPECIALS.ANTIMATTER.radius, {
            label: `orb-${index}`, isStatic, restitution: 0.8, friction: 0.001, density: 0.001
        });
    }
    const orbType = ORB_TYPES[index];
    if (!orbType) return null;
    return Matter.Bodies.circle(x, y, orbType.radius, {
      label: `orb-${index}`, isStatic, restitution: PHYSICS_CONFIG.RESTITUTION, friction: PHYSICS_CONFIG.FRICTION, density: 0.001
    });
  };

  const getCurrentRadius = () => {
    if (nextOrbIndexRef.current === SPECIALS.DEBRIS.id) return SPECIALS.DEBRIS.radius;
    if (nextOrbIndexRef.current === SPECIALS.ANTIMATTER.id) return SPECIALS.ANTIMATTER.radius;
    if (ORB_TYPES[nextOrbIndexRef.current]) return ORB_TYPES[nextOrbIndexRef.current].radius;
    return 15;
  };

  const triggerDrop = useCallback(() => {
    if (!engineRef.current || !canDropRef.current || isPaused || isOverheatedRef.current) return;
    
    const now = Date.now();
    const gap = now - lastDropTimeRef.current;
    lastDropTimeRef.current = now;

    if (gap < PHYSICS_CONFIG.SPAM_WINDOW) {
        spamStreakRef.current++;
    } else {
        spamStreakRef.current = 0;
    }

    if (spamStreakRef.current >= PHYSICS_CONFIG.MAX_SPAM_STREAK) {
        setIsOverheated(true);
        isOverheatedRef.current = true;
        audioManager.playOverheatSound();
        shakeRef.current = 15;
        createFloatingText(aimXRef.current, 60, "SYSTEM OVERHEAT", "#ef4444");
        
        const coolingTimeout = setTimeout(() => {
            setIsOverheated(false);
            isOverheatedRef.current = false;
            spamStreakRef.current = 0;
            setCanDrop(true);
            canDropRef.current = true;
        }, PHYSICS_CONFIG.OVERHEAT_DURATION);
        timeoutsRef.current.push(coolingTimeout);
        
        setCanDrop(false);
        canDropRef.current = false;
        return;
    }

    const x = aimXRef.current;
    const r = getCurrentRadius();
    const clampedX = Math.max(r + 10, Math.min(x, PHYSICS_CONFIG.GAME_WIDTH - r - 10));
    const orb = createOrb(clampedX, 60, nextOrbIndexRef.current);
    
    if (orb) {
        Matter.World.add(engineRef.current.world, orb);
        audioManager.playDropSound();
        addParticle({ x: clampedX, y: 60, vx: 0, vy: -2, life: 0.5, maxLife: 0.5, color: '#ffffff', size: r, type: 'ring' });
    }

    setCanDrop(false);
    canDropRef.current = false;
    
    const queueTimeout = setTimeout(() => {
      if (isOverheatedRef.current) return; 
      const nextIndex = futureOrbIndexRef.current;
      const newFutureIndex = getRandomOrbIndex();
      nextOrbIndexRef.current = nextIndex;
      futureOrbIndexRef.current = newFutureIndex;
      onQueueUpdate(nextIndex, newFutureIndex);
      setCanDrop(true);
      canDropRef.current = true;
      saveGameState();
    }, 650);
    timeoutsRef.current.push(queueTimeout);
  }, [isPaused, onQueueUpdate, saveGameState]);

  const updateAim = useCallback((clientX: number) => {
      if (!canvasRef.current) return;
      const rect = canvasRef.current.getBoundingClientRect();
      const relativeX = (clientX - rect.left) * (PHYSICS_CONFIG.GAME_WIDTH / rect.width);
      const r = getCurrentRadius();
      const clampedX = Math.max(r + 10, Math.min(relativeX, PHYSICS_CONFIG.GAME_WIDTH - r - 10));
      setAimX(clampedX);
      aimXRef.current = clampedX;
  }, []);

  const handleExplosion = (centerX: number, centerY: number, isChain = false) => {
    if (!engineRef.current) return;
    const world = engineRef.current.world;
    const radius = isChain 
        ? PHYSICS_CONFIG.BLAST_RADIUS * PHYSICS_CONFIG.CHAIN_REACTION_MULTIPLIER 
        : PHYSICS_CONFIG.BLAST_RADIUS;

    audioManager.playExplosionSound();
    shakeRef.current = isChain ? 25 : 12;
    flashRef.current = isChain ? 0.4 : 0.2;
    createExplosionEffect(centerX, centerY, isChain ? '#fbbf24' : '#00ffff', isChain ? 2.5 : 1.5);
    
    const bodies = Matter.Composite.allBodies(world);
    const toRemove: Matter.Body[] = [];
    const chainTargets: {x: number, y: number}[] = [];

    bodies.forEach(body => {
        if (!body.label.startsWith('orb-')) return;
        const distVec = Matter.Vector.sub(body.position, {x: centerX, y: centerY});
        const dist = Matter.Vector.magnitude(distVec);

        if (dist < radius) {
            const index = parseInt(body.label.split('-')[1]);
            if (!isChain && index === SPECIALS.DEBRIS.id) {
                chainTargets.push({ x: body.position.x, y: body.position.y });
            }
            if (index >= 6 && index < 100 && !isChain) {
                const forceMag = (1 - dist / radius) * 0.05 * body.mass;
                Matter.Body.applyForce(body, body.position, Matter.Vector.mult(Matter.Vector.normalise(distVec), forceMag));
            } else {
                toRemove.push(body);
                if (index < 100) {
                   const pts = ORB_TYPES[index].score / 2;
                   scoreRef.current += pts;
                   onScoreUpdate(pts);
                }
            }
        }
    });

    Matter.World.remove(world, toRemove);
    chainTargets.forEach((target, i) => {
        const t = setTimeout(() => handleExplosion(target.x, target.y, true), 100 + (i * 100));
        timeoutsRef.current.push(t);
    });
    saveGameState();
  };

  useEffect(() => {
      const handleKeyDown = (e: KeyboardEvent) => {
          if (isPaused) return;
          if (e.key === 'ArrowLeft') {
              const newX = Math.max(0, aimXRef.current - 15);
              setAimX(newX); aimXRef.current = newX;
          } else if (e.key === 'ArrowRight') {
              const newX = Math.min(PHYSICS_CONFIG.GAME_WIDTH, aimXRef.current + 15);
              setAimX(newX); aimXRef.current = newX;
          } else if (e.key === ' ' || e.key === 'ArrowDown' || e.key === 'Enter') {
              triggerDrop();
          }
      };
      window.addEventListener('keydown', handleKeyDown);
      return () => window.removeEventListener('keydown', handleKeyDown);
  }, [triggerDrop, isPaused]);

  useEffect(() => {
    isMountedRef.current = true;
    const canvas = canvasRef.current;
    if (!canvas) return;
    particlesRef.current = [];
    debrisCache.clear();
    const engine = Matter.Engine.create();
    engine.gravity.y = PHYSICS_CONFIG.GRAVITY_Y + (level * 0.1); 

    const ground = Matter.Bodies.rectangle(PHYSICS_CONFIG.GAME_WIDTH / 2, PHYSICS_CONFIG.GAME_HEIGHT + 50, PHYSICS_CONFIG.GAME_WIDTH, 100, { isStatic: true, label: 'ground' });
    const wallL = Matter.Bodies.rectangle(-30, PHYSICS_CONFIG.GAME_HEIGHT/2, 60, PHYSICS_CONFIG.GAME_HEIGHT*2, { isStatic: true, label: 'wall' });
    const wallR = Matter.Bodies.rectangle(PHYSICS_CONFIG.GAME_WIDTH+30, PHYSICS_CONFIG.GAME_HEIGHT/2, 60, PHYSICS_CONFIG.GAME_HEIGHT*2, { isStatic: true, label: 'wall' });
    Matter.World.add(engine.world, [ground, wallL, wallR]);

    if (!loadGameState(Matter.World, engine)) {
        const first = getRandomOrbIndex();
        const second = getRandomOrbIndex();
        nextOrbIndexRef.current = first;
        futureOrbIndexRef.current = second;
        onQueueUpdate(first, second);
        scoreRef.current = 0;
    }

    let renderId: number;
    const ctx = canvas.getContext('2d')!;

    const renderLoop = () => {
        if (!isMountedRef.current) return;
        renderId = requestAnimationFrame(renderLoop);
        if (!isPaused) Matter.Engine.update(engine, 1000 / 60);
        ctx.clearRect(0, 0, canvas.width, canvas.height);
        
        if (shakeRef.current > 0) {
            ctx.save();
            ctx.translate((Math.random() - 0.5) * shakeRef.current, (Math.random() - 0.5) * shakeRef.current);
            shakeRef.current *= 0.9;
            if (shakeRef.current < 0.2) shakeRef.current = 0;
        }
        
        ctx.fillStyle = '#1e1b4b'; ctx.fillRect(0, PHYSICS_CONFIG.GAME_HEIGHT - 6, PHYSICS_CONFIG.GAME_WIDTH, 6);

        if (!isPaused) {
            let x = aimXRef.current;
            const r = getCurrentRadius();
            
            if (isOverheatedRef.current) {
                x += (Math.random() - 0.5) * 8;
                if (Math.random() > 0.8) {
                    addParticle({ 
                        x: x + (Math.random()-0.5)*20, 
                        y: 60 + (Math.random()-0.5)*20, 
                        vx: (Math.random()-0.5)*1, 
                        vy: -Math.random()*2, 
                        life: 0.6, maxLife: 0.6, color: '#ef4444', size: 2+Math.random()*4, type: 'smoke' 
                    });
                }
            }

            ctx.beginPath();
            ctx.setLineDash([5, 5]);
            ctx.moveTo(x, 80 + r);
            ctx.lineTo(x, PHYSICS_CONFIG.GAME_HEIGHT);
            ctx.strokeStyle = isOverheatedRef.current ? 'rgba(239, 68, 68, 0.4)' : 'rgba(255,255,255,0.2)';
            ctx.stroke();
            ctx.setLineDash([]);
            
            if (canDropRef.current || isOverheatedRef.current) {
                ctx.save(); 
                ctx.translate(x, 60); 
                if (isOverheatedRef.current) {
                    ctx.filter = 'drop-shadow(0 0 10px #ef4444)';
                }
                drawOrb(ctx, 0, 0, r, nextOrbIndexRef.current); 
                ctx.restore();
            }
        }

        Matter.Composite.allBodies(engine.world).forEach(body => {
            if (!body.label.startsWith('orb-')) return;
            const index = parseInt(body.label.split('-')[1]);
            ctx.save();
            ctx.translate(body.position.x, body.position.y);
            drawOrb(ctx, 0, 0, getOrbRadius(index), index, body.id, body.velocity, body.angle);
            ctx.restore();
        });

        for (let i = particlesRef.current.length - 1; i >= 0; i--) {
            const p = particlesRef.current[i];
            p.x += p.vx; p.y += p.vy; p.life -= 0.02;
            if (p.life <= 0) { particlesRef.current.splice(i, 1); continue; }
            ctx.globalAlpha = p.life / p.maxLife;
            
            if (p.type === 'text' && p.text) {
                ctx.font = `800 ${p.size}px "Outfit"`;
                ctx.fillStyle = p.color; ctx.textAlign = 'center'; ctx.fillText(p.text, p.x, p.y);
            } else if (p.type === 'sparkle' || p.type === 'smoke') {
                ctx.fillStyle = p.color; ctx.beginPath(); ctx.arc(p.x, p.y, p.size, 0, Math.PI*2); ctx.fill();
            } else if (p.type === 'ring') {
                ctx.strokeStyle = p.color; ctx.lineWidth = 2; ctx.beginPath(); ctx.arc(p.x, p.y, p.size + (1-p.life)*150, 0, Math.PI*2); ctx.stroke();
            }
        }
        
        ctx.globalAlpha = 1.0;
        if (flashRef.current > 0) {
            ctx.fillStyle = `rgba(255,255,255,${flashRef.current})`; ctx.fillRect(0, 0, canvas.width, canvas.height);
            flashRef.current -= 0.05;
        }
        if (shakeRef.current > 0) ctx.restore();
    };
    renderLoop();

    Matter.Events.on(engine, 'collisionStart', (e) => {
        const pairs = e.pairs;
        pairs.forEach(pair => {
            const { bodyA, bodyB } = pair;
            if (!bodyA.label.startsWith('orb-') || !bodyB.label.startsWith('orb-')) return;
            const idxA = parseInt(bodyA.label.split('-')[1]);
            const idxB = parseInt(bodyB.label.split('-')[1]);

            if (idxA === SPECIALS.ANTIMATTER.id || idxB === SPECIALS.ANTIMATTER.id) {
                const impactX = (bodyA.position.x + bodyB.position.x) / 2;
                const impactY = (bodyA.position.y + bodyB.position.y) / 2;
                Matter.World.remove(engine.world, [bodyA, bodyB]);
                handleExplosion(impactX, impactY);
                return;
            }

            if (idxA === SPECIALS.DEBRIS.id || idxB === SPECIALS.DEBRIS.id) return;

            if (idxA === idxB && idxA < ORB_TYPES.length - 1) {
                Matter.World.remove(engine.world, [bodyA, bodyB]);
                const newX = (bodyA.position.x + bodyB.position.x) / 2;
                const newY = (bodyA.position.y + bodyB.position.y) / 2;
                const newIdx = idxA + 1;
                const newOrb = createOrb(newX, newY, newIdx);
                if (newOrb) {
                    Matter.World.add(engine.world, newOrb);
                    Matter.Body.applyForce(newOrb, newOrb.position, {x:0, y:-0.002 * newOrb.mass}); 
                }
                
                const now = Date.now();
                if (now - lastMergeTimeRef.current < 1500) comboCounterRef.current += 1;
                else comboCounterRef.current = 1;
                lastMergeTimeRef.current = now;

                const basePoints = ORB_TYPES[idxA].score * 2;
                const finalPoints = basePoints * comboCounterRef.current;
                onScoreUpdate(finalPoints);
                scoreRef.current += finalPoints;
                audioManager.playMergeSound(idxA);
                createExplosionEffect(newX, newY, ORB_TYPES[idxA].glowColor);
                if (comboCounterRef.current > 1) {
                    createFloatingText(newX, newY, `x${comboCounterRef.current} COMBO`, '#fbbf24');
                }
                saveGameState();
            }
        });
    });

    Matter.Events.on(engine, 'afterUpdate', () => {
        let gameOver = false;
        Matter.Composite.allBodies(engine.world).forEach(b => {
             if(b.label.startsWith('orb-') && b.position.y < 100 && Math.abs(b.velocity.y) < 0.1) gameOver = true;
        });
        if(gameOver && !isPaused && isMountedRef.current) onGameOver();
    });

    engineRef.current = engine;
    return () => {
        isMountedRef.current = false;
        cancelAnimationFrame(renderId);
        Matter.World.clear(engine.world, false);
        Matter.Engine.clear(engine);
        timeoutsRef.current.forEach(clearTimeout);
    };
  }, []);

  const getOrbRadius = (index: number) => {
     if (index === SPECIALS.DEBRIS.id) return SPECIALS.DEBRIS.radius;
     if (index === SPECIALS.ANTIMATTER.id) return SPECIALS.ANTIMATTER.radius;
     return ORB_TYPES[index]?.radius || 15;
  }

  const drawOrb = (ctx: CanvasRenderingContext2D, x: number, y: number, r: number, index: number, bodyId: number = 0, velocity?: {x: number, y: number}, angle: number = 0) => {
    if (index === SPECIALS.DEBRIS.id) {
        ctx.save();
        ctx.rotate(angle);
        const { offsets, craters } = getDebrisVisuals(bodyId, r);
        ctx.beginPath();
        for (let i = 0; i < 6; i++) {
            const a = (i * Math.PI * 2) / 6;
            ctx.lineTo(Math.cos(a) * (r * offsets[i]), Math.sin(a) * (r * offsets[i]));
        }
        ctx.closePath();
        ctx.fillStyle = '#334155'; ctx.fill();
        ctx.strokeStyle = '#1e293b'; ctx.lineWidth = 2; ctx.stroke();
        craters.forEach(c => {
            ctx.beginPath(); ctx.arc(c.x, c.y, c.r, 0, Math.PI*2); 
            ctx.fillStyle = 'rgba(0,0,0,0.3)'; ctx.fill();
        });
        ctx.restore();
    } else if (index === SPECIALS.ANTIMATTER.id) {
        ctx.beginPath(); ctx.arc(0, 0, r + Math.sin(Date.now()*0.01)*2, 0, Math.PI * 2);
        ctx.fillStyle = '#fff'; ctx.shadowColor = '#0ff'; ctx.shadowBlur = 15; ctx.fill();
        ctx.beginPath(); ctx.arc(0, 0, r*0.5, 0, Math.PI*2); ctx.fillStyle="#000"; ctx.fill();
        ctx.shadowBlur = 0;
    } else if (index === 1) { 
        const type = ORB_TYPES[index];
        if (velocity && (Math.abs(velocity.x) > 0.1 || Math.abs(velocity.y) > 0.1)) {
            const speed = Math.sqrt(velocity.x * velocity.x + velocity.y * velocity.y);
            const tailAngle = Math.atan2(velocity.y, velocity.x);
            const tailLength = Math.min(r * 2.5, speed * 10);
            
            ctx.save();
            ctx.rotate(tailAngle + Math.PI); 
            const tailGrad = ctx.createLinearGradient(0, 0, tailLength, 0);
            tailGrad.addColorStop(0, type.glowColor + 'aa');
            tailGrad.addColorStop(1, 'transparent');
            
            ctx.beginPath();
            ctx.moveTo(0, -r * 0.4);
            ctx.lineTo(tailLength, 0);
            ctx.lineTo(0, r * 0.4);
            ctx.fillStyle = tailGrad;
            ctx.fill();
            ctx.restore();
        }

        ctx.save();
        ctx.rotate(angle);
        ctx.beginPath(); ctx.arc(0, 0, r, 0, Math.PI * 2);
        const grad = ctx.createRadialGradient(-r*0.3, -r*0.3, r*0.1, 0, 0, r);
        grad.addColorStop(0, '#fff'); grad.addColorStop(0.3, type.color); grad.addColorStop(1, type.glowColor);
        ctx.fillStyle = grad; 
        ctx.shadowColor = type.glowColor; ctx.shadowBlur = 12;
        ctx.fill();
        ctx.strokeStyle = 'rgba(255,255,255,0.4)'; ctx.lineWidth = 1.5; ctx.stroke();
        ctx.restore();
    } else {
        const type = ORB_TYPES[index];
        ctx.save();
        ctx.rotate(angle);
        ctx.beginPath(); ctx.arc(0, 0, r, 0, Math.PI * 2);
        const grad = ctx.createRadialGradient(-r*0.3, -r*0.3, r*0.1, 0, 0, r);
        grad.addColorStop(0, '#fff'); grad.addColorStop(0.3, type.color); grad.addColorStop(1, type.glowColor);
        ctx.fillStyle = grad; ctx.fill();
        ctx.strokeStyle = 'rgba(255,255,255,0.2)'; ctx.lineWidth = 1; ctx.stroke();
        ctx.restore();
    }
  }

  return (
    <canvas 
        ref={canvasRef}
        width={PHYSICS_CONFIG.GAME_WIDTH}
        height={PHYSICS_CONFIG.GAME_HEIGHT}
        className="w-full h-full block cursor-crosshair touch-none select-none"
        onPointerDown={(e) => { audioManager.init(); updateAim(e.clientX); }}
        onPointerMove={(e) => updateAim(e.clientX)}
        onPointerUp={triggerDrop}
    />
  );
};

export default GameCanvas;